import { AnimatePresence, motion } from 'framer-motion';
import { ImagePlus, Plus } from 'lucide-react';
import type React from 'react';
import { useRef } from 'react';
import { twMerge } from 'tailwind-merge';

import { useFileUploadPreview } from '../../hooks/useFileUploadPreview';
import { typedError } from '../../lib/typedError';
import type { FileInputError } from '../../types/fileUpload';
import { RemoveButton } from '../common/RemoveButton';
import { EASE_OUT } from '../portal/motion';
import { FieldLabel } from './FieldLabel';

interface BaseUploadInputProps {
  name: string;
  label?: string;
  required?: boolean;
  description?: string;
  dimensions?: string;
  value?: File | null;
  onChange: (file: File | null) => void;
  onError?: (error: FileInputError | null) => void;
  className?: string;
  placeholderClassName?: string;
  buttonType?: 'add' | 'upload';
  buttonClassName?: string;
  accept: string;
  acceptedTypes: string[];
  maxSize?: number;
  renderPreview: (url: string, file: File) => React.ReactNode;
  validateFile?: (file: File) => { valid: boolean; error?: FileInputError };
  /** Saved asset shown in the slot until a new file is chosen or it is removed. */
  existingPreview?: React.ReactNode;
  onRemoveExisting?: () => void;
}

/** Slot content swap (placeholder ⇄ preview): fade + 0.96 scale, 150ms in / 120ms out. */
const SLOT_MOTION = {
  initial: { opacity: 0, scale: 0.96 },
  animate: {
    opacity: 1,
    scale: 1,
    transition: { duration: 0.15, ease: EASE_OUT },
  },
  exit: {
    opacity: 0,
    scale: 0.96,
    transition: { duration: 0.12, ease: EASE_OUT },
  },
};

async function cloneFileToMemory(file: File): Promise<File> {
  const buffer = await file.arrayBuffer();
  return new File([buffer], file.name, {
    type: file.type,
    lastModified: file.lastModified,
  });
}

export function BaseUploadInput({
  name,
  label,
  required,
  description,
  dimensions,
  value = null,
  onChange,
  onError,
  className,
  placeholderClassName,
  buttonType = 'add',
  buttonClassName,
  accept,
  acceptedTypes,
  maxSize,
  renderPreview,
  validateFile,
  existingPreview,
  onRemoveExisting,
}: BaseUploadInputProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const {
    previewUrl,
    isDragOver,
    setIsDragOver,
    handleDragOver,
    handleDragLeave,
  } = useFileUploadPreview(value);

  const handleRemove = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    onChange(null);
    onError?.(null);
  };

  const handleRemoveExisting = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    e.stopPropagation();
    onRemoveExisting?.();
  };

  const handlePlaceholderClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileValidation = (file: File): boolean => {
    if (!acceptedTypes.includes(file.type)) {
      onError?.(typedError('InvalidInput', 'File type not supported'));
      return false;
    }

    if (maxSize && file.size > maxSize) {
      onError?.(typedError('FileSizeExceeded', 'File size is too large'));
      return false;
    }

    if (validateFile) {
      const result = validateFile(file);
      if (!result.valid && result.error) {
        onError?.(result.error);
        return false;
      }
    }

    return true;
  };

  const handleDrop = async (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);

    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      const file = files[0];
      if (!handleFileValidation(file)) {
        return;
      }

      try {
        const clonedFile = await cloneFileToMemory(file);
        onChange(clonedFile);
        onError?.(null);
      } catch {
        onChange(null);
        onError?.(
          typedError(
            'InvalidInput',
            'Selected file could not be read. Please re-select it and try again.',
          ),
        );
      }
    }
  };

  const handleInputChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (!handleFileValidation(file)) {
        e.target.value = '';
        return;
      }

      try {
        const clonedFile = await cloneFileToMemory(file);
        onChange(clonedFile);
        onError?.(null);
      } catch {
        e.target.value = '';
        onChange(null);
        onError?.(
          typedError(
            'InvalidInput',
            'Selected file could not be read. Please re-select it and try again.',
          ),
        );
      }
    } else {
      onChange(null);
      onError?.(null);
    }
  };

  const Icon = buttonType === 'upload' ? ImagePlus : Plus;
  const hasFile = Boolean(previewUrl && value);
  const showExisting = !hasFile && Boolean(existingPreview);

  return (
    <div className={twMerge('flex-1 flex flex-col gap-1', className)}>
      {(label || dimensions || description) && (
        <FieldLabel
          label={label}
          required={required}
          detail={dimensions}
          explanation={description}
        />
      )}

      <div className={twMerge('relative', placeholderClassName)}>
        <input
          ref={fileInputRef}
          type="file"
          accept={accept}
          name={name}
          aria-label={`Upload ${label || name}`}
          className="hidden"
          onChange={handleInputChange}
        />
        <AnimatePresence initial={false} mode="popLayout">
          {hasFile || showExisting ? (
            <motion.div
              key={hasFile ? 'file' : 'existing'}
              {...SLOT_MOTION}
              className="relative h-full w-full"
            >
              <div className="h-full w-full overflow-hidden rounded-lg">
                {previewUrl && value
                  ? renderPreview(previewUrl, value)
                  : existingPreview}
              </div>
              <RemoveButton
                onClick={hasFile ? handleRemove : handleRemoveExisting}
                ariaLabel={`Remove ${label || name}`}
              />
            </motion.div>
          ) : (
            <motion.div
              key="placeholder"
              {...SLOT_MOTION}
              className="relative h-full w-full"
            >
              <div
                onClick={handlePlaceholderClick}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                className={twMerge(
                  'w-full h-full flex items-center justify-center cursor-pointer bg-transparent hover:bg-white/[0.02]',
                  'border border-dashed border-seeker-700 rounded-lg transition-[border-color,background-color] duration-150 ease-out hover:border-seeker-500',
                  'focus-visible:outline-none focus-visible:border-white',
                  isDragOver && 'border-white bg-white/5',
                  placeholderClassName,
                )}
                role="button"
                aria-label={`Upload ${label || name}`}
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ')
                    handlePlaceholderClick();
                }}
              >
                <div
                  className={twMerge(
                    'flex size-8 items-center justify-center rounded-lg text-white drop-shadow-[0_0_10px_rgba(0,0,0,0.2)] pointer-events-none',
                    buttonClassName,
                  )}
                >
                  <Icon className="size-4" />
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}
